import {
  Box,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { latest } from '../types';
import type { ClientAcquisitionPoint, SafeTOrgKey } from '../types';
import { SafeTCardShell } from './SafeTCardShell';
import { safeTColors } from '../safeTColors';
import {
  getCombinedAnnualGoalsForYear,
  getDronePortAnnualGoalsForYear,
  getSkywayAnnualGoalsForYear,
  parseQuarterLabel,
  COMBINED_LONG_TERM_GOALS,
  DRONEPORT_LONG_TERM_GOALS,
  SKYWAY_LONG_TERM_GOALS,
} from '../benchmarks';
import { getMultiGoalStatus, GoalState, getAnnualPaceStatus } from '../../statusUtils';
import { MetricRow } from '../../atlas/cards/MetricRow';

type Props =
  | { mode: 'combined'; series: ClientAcquisitionPoint[] }
  | { mode: 'org'; orgKey: SafeTOrgKey; series: ClientAcquisitionPoint[] };

function getGoalsForScope(props: Props) {
  if (props.mode === 'combined') return { getGoals: getCombinedAnnualGoalsForYear, longTerm: COMBINED_LONG_TERM_GOALS };
  return props.orgKey === 'skyway'
    ? { getGoals: getSkywayAnnualGoalsForYear, longTerm: SKYWAY_LONG_TERM_GOALS }
    : { getGoals: getDronePortAnnualGoalsForYear, longTerm: DRONEPORT_LONG_TERM_GOALS };
}

export function ClientAcquisitionCard(props: Props) {
  const theme = useMantineTheme();
  const { getGoals: getAnnualGoalsForYear, longTerm: longTermGoals } = getGoalsForScope(props);
  const series = props.series;
  const isEmpty = !series?.length;
  const cur = isEmpty ? null : latest(series);
  const curQuarterMeta = parseQuarterLabel(cur?.quarter ?? '');
  const curYear = curQuarterMeta?.year ?? null;
  const curQuarterNum = curQuarterMeta?.quarter ?? null;

  const ytdEngaged =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.engaged, 0);

  const ytdAcquired =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.acquired, 0);

  const prevYearYtdEngaged =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.engaged, 0);

  const prevYearYtdAcquired =
    curYear === null || curQuarterNum === null
      ? null
      : series
          .filter((p) => {
            const m = parseQuarterLabel(p.quarter);
            return m?.year === curYear - 1 && m.quarter <= curQuarterNum;
          })
          .reduce((acc, p) => acc + p.acquired, 0);

  const engagedYoyPct =
    ytdEngaged !== null && prevYearYtdEngaged !== null && prevYearYtdEngaged !== 0
      ? ((ytdEngaged - prevYearYtdEngaged) / prevYearYtdEngaged) * 100
      : null;
  const acquiredYoyPct =
    ytdAcquired !== null && prevYearYtdAcquired !== null && prevYearYtdAcquired !== 0
      ? ((ytdAcquired - prevYearYtdAcquired) / prevYearYtdAcquired) * 100
      : null;
  const curYtdConversion =
    ytdEngaged !== null && ytdAcquired !== null && ytdEngaged > 0
      ? (ytdAcquired / ytdEngaged) * 100
      : null;
  const prevYtdConversion =
    prevYearYtdEngaged !== null && prevYearYtdAcquired !== null && prevYearYtdEngaged > 0
      ? (prevYearYtdAcquired / prevYearYtdEngaged) * 100
      : null;
  const conversionYoyPct =
    curYtdConversion !== null && prevYtdConversion !== null && prevYtdConversion !== 0
      ? ((curYtdConversion - prevYtdConversion) / prevYtdConversion) * 100
      : null;
  const yoyArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');

  const annualEngagedTotals = series.reduce<Record<number, number>>((acc, p) => {
    const meta = parseQuarterLabel(p.quarter);
    if (!meta) return acc;
    acc[meta.year] = (acc[meta.year] ?? 0) + p.engaged;
    return acc;
  }, {});

  const annualAcquiredTotals = series.reduce<Record<number, number>>((acc, p) => {
    const meta = parseQuarterLabel(p.quarter);
    if (!meta) return acc;
    acc[meta.year] = (acc[meta.year] ?? 0) + p.acquired;
    return acc;
  }, {});

  const dataYears = Array.from(
    new Set(series.map((p) => parseQuarterLabel(p.quarter)?.year).filter((y): y is number => y != null)),
  );
  const minYear = dataYears.length > 0 ? Math.min(...dataYears) : curYear ?? 2024;
  const maxYear = dataYears.length > 0 ? Math.max(...dataYears) : curYear ?? minYear;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  const curYearGoals = curYear !== null ? getAnnualGoalsForYear(curYear) : { engaged: undefined, acquired: undefined };

  const annualEngagedData = years.map((year) => {
    const goals = getAnnualGoalsForYear(year);
    return {
      year: String(year),
      engaged: annualEngagedTotals[year] ?? 0,
      engagedGoal: goals.engaged,
    };
  });
  const annualAcquiredData = years.map((year) => {
    const goals = getAnnualGoalsForYear(year);
    return {
      year: String(year),
      acquired: annualAcquiredTotals[year] ?? 0,
      acquiredGoal: goals.acquired,
    };
  });
  const engagedMax = Math.max(longTermGoals.engagedPerYear, ...annualEngagedData.map((d) => d.engaged), 1);
  const acquiredMax = Math.max(longTermGoals.acquiredPerYear, ...annualAcquiredData.map((d) => d.acquired), 1);

  const status = (() => {
    if (ytdEngaged === null || ytdAcquired === null) return { label: 'Snapshot' as const, color: 'gray' as const, tooltip: undefined as string | undefined };
    const engagedGoal = curYearGoals.engaged;
    const acquiredGoal = curYearGoals.acquired;
    const states: GoalState[] = [];
    if (engagedGoal !== undefined) {
      const s = getAnnualPaceStatus({ ytd: ytdEngaged, goalPerYear: engagedGoal, quarterLabel: cur?.quarter ?? 'Q1 1970', direction: 'min' });
      states.push(s.label === 'Achieved' || s.label === 'On track' ? 'on_track' : 'off_track');
    }
    if (acquiredGoal !== undefined) {
      const s = getAnnualPaceStatus({ ytd: ytdAcquired, goalPerYear: acquiredGoal, quarterLabel: cur?.quarter ?? 'Q1 1970', direction: 'min' });
      states.push(s.label === 'Achieved' || s.label === 'On track' ? 'on_track' : 'off_track');
    }
    if (states.length === 0) return { label: 'Snapshot' as const, color: 'gray' as const, tooltip: undefined as string | undefined };
    return getMultiGoalStatus(states);
  })();

  const tooltipStyles = {
    contentStyle: {
      backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
      border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
      borderRadius: 10,
      color: theme.colorScheme === 'dark' ? '#fff' : '#000',
      padding: '10px 12px',
      maxWidth: 320,
      whiteSpace: 'normal' as const,
      wordBreak: 'break-word' as const,
    },
    labelStyle: { color: theme.colorScheme === 'dark' ? '#fff' : '#000' },
    itemStyle: { color: theme.colorScheme === 'dark' ? '#fff' : '#000' },
  } as const;

  const sharedTooltip = (label: string, value: number, goalLabel: string) => (
    <div style={tooltipStyles.contentStyle as any}>
      <div style={{ ...(tooltipStyles.labelStyle as any), fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
      <div style={{ ...(tooltipStyles.itemStyle as any), fontWeight: 900, fontSize: 14, lineHeight: 1.2, marginBottom: 4 }}>{value}</div>
      <div style={{ ...(tooltipStyles.itemStyle as any), fontSize: 12, opacity: 0.9 }}>{goalLabel}</div>
    </div>
  );

  return (
    <SafeTCardShell
      title="Client Acquisition"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
          <MetricRow
            items={[
              {
                label: 'Engaged (YTD)',
                labelTooltip: 'Year-to-date potential clients engaged.',
                value: <Text size="lg" weight={800}>{ytdEngaged ?? '—'}</Text>,
                sublabel:
                  engagedYoyPct !== null || curYearGoals.engaged !== undefined ? (
                    <>
                      {engagedYoyPct !== null && (
                        <>
                          {engagedYoyPct === 0 ? '0.00% YoY' : `${yoyArrow(engagedYoyPct)}${Math.abs(engagedYoyPct).toFixed(2)}% YoY`}
                          <br />
                        </>
                      )}
                      {curYearGoals.engaged !== undefined ? `Goal ${curYearGoals.engaged}/year` : null}
                    </>
                  ) : undefined,
              },
              {
                label: 'Acquired (YTD)',
                labelTooltip: 'Year-to-date clients acquired.',
                value: <Text size="lg" weight={800}>{ytdAcquired ?? '—'}</Text>,
                sublabel:
                  acquiredYoyPct !== null || curYearGoals.acquired !== undefined ? (
                    <>
                      {acquiredYoyPct !== null && (
                        <>
                          {acquiredYoyPct === 0 ? '0.00% YoY' : `${yoyArrow(acquiredYoyPct)}${Math.abs(acquiredYoyPct).toFixed(2)}% YoY`}
                          <br />
                        </>
                      )}
                      {curYearGoals.acquired !== undefined ? `Goal ${curYearGoals.acquired}/year` : null}
                    </>
                  ) : undefined,
              },
              {
                label: 'Conversion (YTD)',
                labelTooltip: 'Percentage of YTD engaged clients that were acquired.',
                value: (
                  <Text size="lg" weight={800}>
                    {curYtdConversion !== null ? `${Math.round(curYtdConversion)}%` : '—'}
                  </Text>
                ),
                sublabel:
                  conversionYoyPct !== null
                    ? (conversionYoyPct === 0 ? '0.00% YoY' : `${yoyArrow(conversionYoyPct)}${Math.abs(conversionYoyPct).toFixed(2)}% YoY`)
                    : undefined,
              },
            ]}
          />

          <Stack spacing={4} sx={{ marginTop: '0.75rem' }}>
            <Stack spacing={4}>
              <Text size="xs" color="dimmed">Annual Engagements</Text>
              <Box sx={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={annualEngagedData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                    <YAxis
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                      tickMargin={8}
                      domain={[0, engagedMax]}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const goalLabel = p.engagedGoal != null ? `Goal ${p.engagedGoal}/year` : '—';
                        return sharedTooltip(label, p.engaged, goalLabel);
                      }}
                    />
                    <ReferenceLine
                      y={longTermGoals.engagedPerYear}
                      stroke={safeTColors.goal}
                      strokeWidth={2}
                      strokeDasharray="6 6"
                      ifOverflow="extendDomain"
                    />
                    <Bar dataKey="engaged" name="Engaged" fill="#228be6" radius={[6, 6, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Stack>
            <Stack spacing={4}>
              <Text size="xs" color="dimmed">Annual Acquisitions</Text>
              <Box sx={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={annualAcquiredData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                    <YAxis
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      width={44}
                      tickMargin={8}
                      domain={[0, acquiredMax]}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const goalLabel = p.acquiredGoal !== undefined ? `Goal ${p.acquiredGoal}/year` : '—';
                        return sharedTooltip(label, p.acquired, goalLabel);
                      }}
                    />
                    <ReferenceLine
                      y={longTermGoals.acquiredPerYear}
                      stroke={safeTColors.goal}
                      strokeWidth={2}
                      strokeDasharray="6 6"
                      ifOverflow="extendDomain"
                    />
                    <Bar dataKey="acquired" name="Acquired" fill="#228be6" radius={[6, 6, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Stack>
          </Stack>
        </Stack>
    </SafeTCardShell>
  );
}
